import {sortBy} from 'lodash';
import React, {FC, ReactNode, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {CustomFieldAttribute} from '../../../../../libs/shared/core/src/models/api/customFieldAttributeModel';
import {AccountDomainAssociationJobModel} from '../../../../../libs/shared/core/src/models/api/jobModel';
import {isJobRunning} from '../../../../../libs/shared/core/src/models/helpers/jobHelpers';
import {ResourceMap} from '../../../../../libs/shared/core/src/store/commonAppTypes';
import {ContactOrAccountDetailsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownCategory} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SearchDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  AccountAttributeUpdateAction,
  addCustomAttribute,
  addDescription,
} from '../../../../helpers/models/accountAttributeUpdateHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {CustomFieldModel} from '../../../../models/api/customFieldModel';
import {TypedRowValue} from '../../../common/dropdowns/source/dropdownSource';
import {EmailDomainDropdownModesEnum} from './emailDomainDropdown';
import {EmailDomainDropdownContainer} from './emailDomainDropdownContainer';

/*
 * Constants.
 */

const dropdownHeight = 340;
const dropdownWidth = 260;

/*
 * Types.
 */

enum DropdownItemTypesEnum {
  DEFAULT_CATEGORY = 0,
  DEFAULT_FIELD = 1,
  CUSTOM_CATEGORY = 2,
  CUSTOM_FIELD = 3,
  ALL_FIELDS_ADDED = 4,
}

interface DropdownItemField {
  searchName: string;
  content: ReactNode;
  onSelectHandler: (event: FrontEvent) => void;
  canConflict: boolean;
}

type DefaultCategoryRow = TypedRowValue<DropdownItemTypesEnum.DEFAULT_CATEGORY, ReactNode>;
type DefaultFieldRow = TypedRowValue<DropdownItemTypesEnum.DEFAULT_FIELD, DropdownItemField>;
type CustomCategoryRow = TypedRowValue<DropdownItemTypesEnum.CUSTOM_CATEGORY, ReactNode>;
type CustomFieldRow = TypedRowValue<DropdownItemTypesEnum.CUSTOM_FIELD, DropdownItemField>;
type AllFieldsAddedRow = TypedRowValue<DropdownItemTypesEnum.ALL_FIELDS_ADDED, ReactNode>;

type AddInformationDropdownTypedRowValue =
  | DefaultCategoryRow
  | DefaultFieldRow
  | CustomCategoryRow
  | CustomFieldRow
  | AllFieldsAddedRow;

interface AddAccountInformationPopoverProps {
  account?: AccountModel;
  panelId?: ContactOrAccountDetailsPanelId;
  customFieldsMap: ResourceMap<CustomFieldModel>;
  existingCustomAttributes: ReadonlyArray<CustomFieldAttribute>;
  onAddInformation: (action: AccountAttributeUpdateAction) => void;
  isUserGlobalAdmin: boolean;
  onNavigateToCustomFieldsSettings: () => void;
  buttonText: ReactNode;
  jobs: ReadonlyArray<AccountDomainAssociationJobModel>;
}

/*
 * Styles.
 */

const StyledPopoverButton = styled(Button)`
  margin: -5px -12px -4px 0;
`;

const StyledIcon = styled(Icon)`
  margin-left: 2px;
  margin-right: 12px;
`;

const StyledDropdownItem = styled(DropdownItem)<{isDisabled?: boolean}>`
  ${(p) =>
    p.isDisabled &&
    css`
      cursor: not-allowed;
    `}
`;

const StyledDropdownItemContent = styled(DropdownItemContent)<{isDisabled?: boolean}>`
  ${flexCenter}
  ${(p) =>
    p.isDisabled &&
    css`
      cursor: not-allowed;
    `}
`;

const StyledAddCustomFieldButtonWrapperDiv = styled.div`
  position: absolute;
  right: 5px;
  top: 2px;
`;

const StyledDropdownCategory = styled(DropdownCategory)`
  ${flexCenter}
`;

const StyledAllFieldsAddedDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  padding: 6px 0 4px 12px;
`;

/*
 * Component.
 */

export const AddAccountInformationPopover: FC<AddAccountInformationPopoverProps> = (props) => {
  const {account, panelId, buttonText, jobs} = props;
  const [isAddingDomains, setIsAddingDomains] = useState<boolean>(false);

  const onEmailDomainSelect = () => setIsAddingDomains(true);

  const [query, setQuery] = useState('');
  const data = getDropdownItems(props, onEmailDomainSelect);
  const filteredData = rowFilter(data, query);

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <StyledPopoverButton variant={ButtonVariantsEnum.LINK} hasHover={isOpen}>
          {buttonText}
        </StyledPopoverButton>
      )}
      renderModal={(popoverProps) =>
        isAddingDomains && account && panelId ? (
          <EmailDomainDropdownContainer
            panelId={panelId}
            mode={EmailDomainDropdownModesEnum.ADD}
            account={account}
            popoverTransferProps={popoverProps}
            onCancel={() => setIsAddingDomains(false)}
            // Close the modal after the user saves
            onSave={() => {
              popoverProps.onRequestClose();
              setIsAddingDomains(false);
            }}
          />
        ) : (
          <SearchDropdown
            {...popoverProps}
            data={filteredData}
            query={query}
            onQueryChange={setQuery}
            renderRow={(item) => renderRow(item, jobs, account)}
            headerTitle={<Trans id="account-details-add-information-dropdown-title">Add information</Trans>}
            height={dropdownHeight}
            width={dropdownWidth}
          />
        )
      }
    />
  );
};

/*
 * Helpers.
 */

function getDropdownItems(
  props: AddAccountInformationPopoverProps,
  onEmailDomainSelect: () => void,
): ReadonlyArray<AddInformationDropdownTypedRowValue> {
  const {
    account,
    customFieldsMap,
    existingCustomAttributes,
    onAddInformation,
    isUserGlobalAdmin,
    onNavigateToCustomFieldsSettings,
  } = props;
  if (account) {
    return [
      buildDefaultFieldCategoryItem(),
      ...buildDefaultFieldItems(account, onAddInformation, onEmailDomainSelect),
      buildCustomFieldCategoryItem(isUserGlobalAdmin, onNavigateToCustomFieldsSettings),
      ...buildCustomFieldItems(existingCustomAttributes, customFieldsMap, onAddInformation),
    ];
  }

  return [
    buildCustomFieldCategoryItem(isUserGlobalAdmin, onNavigateToCustomFieldsSettings),
    ...buildCustomFieldItems(existingCustomAttributes, customFieldsMap, onAddInformation),
  ];
}

function rowFilter(items: ReadonlyArray<AddInformationDropdownTypedRowValue>, query: string) {
  if (!query) {
    return items;
  }

  const queryInLowerCase = query.toLowerCase();
  const defaultCategoryItem = items.find((item) => item.type === DropdownItemTypesEnum.DEFAULT_CATEGORY);
  const filteredDefaultItems = items.filter(
    (item) =>
      item.type === DropdownItemTypesEnum.DEFAULT_FIELD &&
      item.value.searchName.toLowerCase().includes(queryInLowerCase),
  );
  const customCategoryItem = items.find((item) => item.type === DropdownItemTypesEnum.CUSTOM_CATEGORY);
  const filteredCustomItems = items.filter(
    (item) =>
      item.type === DropdownItemTypesEnum.CUSTOM_FIELD &&
      item.value.searchName.toLowerCase().includes(queryInLowerCase),
  );

  // Only show the category header if the query matches items in that category
  return [
    filteredDefaultItems.length > 0 ? defaultCategoryItem : undefined,
    ...filteredDefaultItems,
    filteredCustomItems.length > 0 ? customCategoryItem : undefined,
    ...filteredCustomItems,
  ].filter(isDefined);
}

function renderRow(
  item: AddInformationDropdownTypedRowValue,
  jobs: ReadonlyArray<AccountDomainAssociationJobModel>,
  account?: AccountModel,
) {
  switch (item.type) {
    case DropdownItemTypesEnum.DEFAULT_CATEGORY:
    case DropdownItemTypesEnum.CUSTOM_CATEGORY:
      return <StyledDropdownCategory>{item.value}</StyledDropdownCategory>;

    case DropdownItemTypesEnum.ALL_FIELDS_ADDED:
      return item.value;

    case DropdownItemTypesEnum.DEFAULT_FIELD:
    case DropdownItemTypesEnum.CUSTOM_FIELD:
      if (item.value.canConflict) {
        const isDisabled = computeIsConflict(jobs, account);
        return (
          <TooltipCoordinator render={() => renderTooltip(jobs, account)}>
            <StyledDropdownItem
              onSelect={(event) => {
                item.value.onSelectHandler(event);
              }}
              isDisabled={isDisabled}
            >
              <StyledDropdownItemContent isDisabled={isDisabled}>
                {item.value.content}
              </StyledDropdownItemContent>
            </StyledDropdownItem>
          </TooltipCoordinator>
        );
      }
      return (
        <DropdownItem
          onSelect={(event) => {
            item.value.onSelectHandler(event);
          }}
        >
          <StyledDropdownItemContent>{item.value.content}</StyledDropdownItemContent>
        </DropdownItem>
      );

    default:
      return null;
  }
}

function computeIsConflict(
  jobs: ReadonlyArray<AccountDomainAssociationJobModel>,
  account?: AccountModel,
): boolean {
  // Filter down to Jobs which are active and on the currently selected Account
  if (!account) {
    return false;
  }
  const conflictingJobs = jobs.filter((job) => job.accountId === account.id).filter(isJobRunning);
  return conflictingJobs.length > 0;
}

function renderTooltip(jobs: ReadonlyArray<AccountDomainAssociationJobModel>, account?: AccountModel) {
  if (computeIsConflict(jobs, account)) {
    return <Trans id="account-details-email-domains-busy">Associating contacts, please wait...</Trans>;
  }

  return null;
}

function buildDefaultFieldCategoryItem(): DefaultCategoryRow {
  return {
    type: DropdownItemTypesEnum.DEFAULT_CATEGORY,
    value: <Trans id="account-details-add-information-default-header">Default</Trans>,
  };
}

function buildDefaultFieldItems(
  account: AccountModel,
  onAddInformation: (action: AccountAttributeUpdateAction) => void,
  onEmailDomainSelect: () => void,
) {
  const defaultFieldItems: Array<DefaultFieldRow | AllFieldsAddedRow> = [];

  if (account.domains.length === 0) {
    defaultFieldItems.push(
      buildDefaultFieldItem(
        'Email domains',
        'mentionSmall',
        <Trans id="account-details-add-information-email-domains">Email domains</Trans>,
        (event: FrontEvent) => {
          // Prevent the dropdown from closing when selecting the dropdown item
          event.preventDefault();
          onEmailDomainSelect();
        },
        true, // canConflict, so renderRow must check for active domain association Jobs and disable this option
      ),
    );
  }

  if (account.description === undefined) {
    defaultFieldItems.push(
      buildDefaultFieldItem(
        'Description',
        'notesSmall',
        <Trans id="account-details-add-information-description">Description</Trans>,
        () => onAddInformation(addDescription('')),
        false,
      ),
    );
  }

  // If all the fields are added, show a message for that.
  if (defaultFieldItems.filter(isDefined).length === 0) {
    return [buildAllFieldsAddedRow()];
  }

  return defaultFieldItems.filter(isDefined);
}

function buildDefaultFieldItem(
  searchName: string,
  iconName: IconName,
  display: ReactNode,
  onSelectHandler: (event: FrontEvent) => void,
  canConflict: boolean,
): DefaultFieldRow {
  return {
    type: DropdownItemTypesEnum.DEFAULT_FIELD,
    value: {
      searchName,
      content: (
        <>
          <UserThemeConsumer>
            {(theme) => <StyledIcon name={iconName} size={16} color={theme.greys.shade70} />}
          </UserThemeConsumer>
          {display}
        </>
      ),
      onSelectHandler,
      canConflict,
    },
  };
}

function buildCustomFieldCategoryItem(
  isUserGlobalAdmin: boolean,
  onNavigateToCustomFieldsSettings: () => void,
): CustomCategoryRow {
  return {
    type: DropdownItemTypesEnum.CUSTOM_CATEGORY,
    value: (
      <>
        <Trans id="account-details-add-information-custom-header">Custom</Trans>
        {isUserGlobalAdmin && (
          <StyledAddCustomFieldButtonWrapperDiv>
            <Button
              variant={ButtonVariantsEnum.LINK}
              size={VisualSizesEnum.SMALL}
              onClick={() => onNavigateToCustomFieldsSettings()}
            >
              <Trans id="account-details-custom-add-button-label">Add</Trans>
            </Button>
          </StyledAddCustomFieldButtonWrapperDiv>
        )}
      </>
    ),
  };
}

function buildCustomFieldItems(
  existingCustomAttributes: ReadonlyArray<CustomFieldAttribute>,
  customFieldsMap: ResourceMap<CustomFieldModel>,
  onAddInformation: (action: AccountAttributeUpdateAction) => void,
) {
  const customFields = Object.values(customFieldsMap).filter(isDefined);
  const alreadyUsedCustomFieldIds = new Set(existingCustomAttributes?.map((attribute) => attribute.id));
  const unusedCustomFields = customFields.filter(
    (customField) => !alreadyUsedCustomFieldIds.has(customField.id),
  );
  const customFieldItems: Array<CustomFieldRow> = unusedCustomFields.map((field) => ({
    type: DropdownItemTypesEnum.CUSTOM_FIELD,
    value: {
      searchName: field.name,
      content: field.name,
      onSelectHandler: () => onAddInformation(addCustomAttribute('', field)),
      canConflict: false,
    },
  }));

  // If all the fields are added, show a message for that.
  if (customFieldItems.length === 0) {
    return [buildAllFieldsAddedRow()];
  }

  return sortBy(customFieldItems, (item) => item.value.searchName.toLowerCase());
}

function buildAllFieldsAddedRow(): AllFieldsAddedRow {
  return {
    type: DropdownItemTypesEnum.ALL_FIELDS_ADDED,
    value: (
      <StyledAllFieldsAddedDiv>
        <Trans id="account-details-add-information-all-fields-added">All fields added</Trans>
      </StyledAllFieldsAddedDiv>
    ),
  };
}
